/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useContext, useEffect, useState} from "react";
import ReservationDetailInput
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailInput";
import clsx from "clsx";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone-lite";
import {useTranslation} from "react-i18next";
import {UserType} from "types/enums/UserType";
import {CountryCode, parsePhoneNumber} from "libphonenumber-js";
import {countryList, filteredSpecificCountryFirst, PhoneCountry} from "utils/countryUtils";
import dayjs from "dayjs";
import {DEFAULT_COUNTRY_CODE, MIN_PERSON_BIRTHDATE_AGE} from "helpers/config";
import BookingLoginAction, {
    SetCustomerToReservationNewRequest,
    UpdateCustomerFromReservationNewRequest,
} from "store/bookingLogin/BookingLoginAction";
import {useDispatch} from "useDispatch";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {consoleLog} from "utils/utility";
import Dates from "utils/Dates";
import {useTypedSelector} from "helpers/reducers";
import BookingLoginTableRow from "pages/Front/Booking/BookingLogin/components/BookingLoginTableRow";

import {ReservationDetailContextType} from "types/entities/specific/ReservationDetailContextType";
import PresetPersonTypeOption
    from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/PresetPersonTypeOption";
import {usePersonTypeOptions} from "utils/hooks/usePersonTypeOptions";
import ReservationDetailCountry
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailCountry";
import ReservationDetailPhoneWithCountrySelect
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailPhoneWithCountrySelect";
import {CheckIcon} from "@heroicons/react/24/outline";
import BookingLoginReservationButton
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import TableRowColumn from "pages/Front/Booking/BookingLogin/components/TableRowColumn";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import LoadingIcon from "components/Icons/LoadingIcon";
import {
    BookingLoginReservationDetailDriverRedesignContext,
} from "pages/Front/Booking/BookingLogin/BookingLoginReservationDetailRedesign";
import ReservationButtonInner
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationButtonInner";
import Birthdate from "pages/Front/Registration/components/Birthdate";
import FeatherIcon from "components/FeatherIcon";
import {PhoneNumberType, PhoneNumberUtil} from "google-libphonenumber";
import BookingLoginTableRowColumn from "../BookingLoginTableRowColumn";
import ArrowRightIcon from "@heroicons/react/24/solid/ArrowRightIcon";

const REGEX_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

export interface LoginReservationEmailCheckInputs {
    emailCheck: string;
}

export interface LoginReservationCustomerFormInputs {
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    address: string;
    zip: string;
    town: string;
    country: string;
    email?: string;
    phone?: string;
    packages_cart_item_id: number | null;
    parent_user_id: number | null;
}

const AccessTicketEditGuest = (props: {reservationDriver: ReservationDriver | null; context: ReservationDetailContextType; item: PackagesCartItem}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const phoneUtil = PhoneNumberUtil.getInstance();
    const session = useTypedSelector(state => state.sessionState.session);
    const context = useContext(BookingLoginReservationDetailDriverRedesignContext);
    const selectedReservation = useTypedSelector(state => state.bookingLoginState.selectedReservation);
    const user = useTypedSelector(state => state.bookingLoginState.user);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [phoneNumberWasSuggested, setPhoneNumberWasSuggested] = useState<boolean>(false);
    const isMainUser: boolean = user?.id === props?.reservationDriver?.driver?.id;

    const customerUserTypeMain = context?.driversInReservation.find(driver => driver.driver && driver.driver.user_type == UserType.User);
    const reservationHaveCustomerUserTypeMain: boolean = !!customerUserTypeMain;
    const isExistingCustomer: boolean = typeof props.reservationDriver?.driver !== "undefined";
    const isCustomerUserTypeMain: boolean = isExistingCustomer
        ? props?.reservationDriver?.driver?.user_type == UserType.User
        : !reservationHaveCustomerUserTypeMain;
    // const isDisabled: boolean = context?.showModal ? false : isExistingCustomer && !isEditing;
    const isDisabled: boolean = false;

    const defaultCountry = countryList.find(item => item.textCode === DEFAULT_COUNTRY_CODE) ?? countryList[161];
    const driverPhonePrefix = props?.reservationDriver?.driver?.phone ? parsePhoneNumber(props.reservationDriver.driver.phone)?.countryCallingCode : 41;
    const defaultPhoneCountry = countryList.find(item => {
        if (Array.isArray(item.shortCode)) {
            return item.shortCode.includes(driverPhonePrefix as number);
        }
        return Number(item.shortCode) == driverPhonePrefix;
    });
    const propsDefaultCountry = countryList.find(item => item.name === props.reservationDriver?.driver?.country);
    const defaultCountryForSelect: PhoneCountry = propsDefaultCountry ? propsDefaultCountry : defaultCountry;
    const [selectedCountryForPhone, setSelectedCountryForPhone] = useState<PhoneCountry>(defaultPhoneCountry ?? defaultCountry);

    const [resetCountry, setResetCountry] = useState<boolean>(false);
    const [resetPhone, setResetPhone] = useState<boolean>(false);
    const [emailCheckInfoMessage, setEmailCheckInfoMessage] = useState<string | null>(null);
    const maximumDate = dayjs().subtract(MIN_PERSON_BIRTHDATE_AGE, "year").toDate();
    const [isChild, setIsChild] = useState<boolean>(props.item.packageItem?.person_type == "child");
    const minimalDate = dayjs()
        .subtract(isChild ? 18 : 100, "year")
        .toDate();
    const [showGuestPhoneAndEmail, setShowGuestPhoneAndEmail] = useState<boolean>(true);

    const AccountEmailCheckSchema = yup.object().shape({
        emailCheck: yup
            .string()
            .required("Email is required field.")
            .test("email", "Invalid email format", value => {
                return REGEX_EMAIL.test(value);
            }),
    });

    const getRequiredFields = () => {
        const requiredFields: string[] = [];

        requiredFields.push("name");
        requiredFields.push("last_name");
        requiredFields.push("email");

        if (isMainUser) {
            requiredFields.push("phone");
            requiredFields.push("nickname");
            requiredFields.push("birthdate");
            requiredFields.push("address");
            requiredFields.push("zip");
            requiredFields.push("town");
            requiredFields.push("country");
        } else if (isChild) {
            requiredFields.push("birthdate");
        }

        // if (isExistingCustomer) {
        //     requiredFields.push("phone");
        //     requiredFields.push("nickname");
        //     requiredFields.push("birthdate");
        //     requiredFields.push("address");
        //     requiredFields.push("zip");
        //     requiredFields.push("town");
        //     requiredFields.push("country");
        // }
        return requiredFields;
    };

    const requiredFields = getRequiredFields();

    const returnRequiredFieldByFieldName = (fieldName: string) => {
        const requiredField = FIELD_VALIDATIONS.find(validation => (validation.field = fieldName));
        if (!requiredField) {
            return t("Field is required");
        }
        return requiredField.messageKey;
    };

    const requiredOnExisting = (field: string) => {
        return {
            is: true,
            then: (schema: any) => {
                // If the field is not in requiredFields, return the schema without required validation
                if (!requiredFields.includes(field)) {
                    console.log("field is not in requiredFields", field);
                    return schema;
                }
                // If it is required, add the required validation with the appropriate message
                return schema.required(returnRequiredFieldByFieldName(field));
            },
        };
    };

    const AccountMainSchema = yup.object().shape({
        id: yup.number().nullable(),
        name: yup.string().when("id", requiredOnExisting("name")),
        last_name: yup.string().when("id", requiredOnExisting("last_name")),
        nickname: yup
            .string()
            .nullable()
            .test("main-user-nickname-required", "Nickname is required.", function (value) {
                return isMainUser ? !!value?.trim() : true;
            }),
        address: yup.string().nullable(),
        zip: yup.string().nullable(),
        town: yup.string().nullable(),
        country: yup.string().nullable(),
        birthdate: yup
            .date()
            .when("id", requiredOnExisting("birthdate"))
            .test("main-user-birthdate-required", "Birthdate is required field.", function (value) {
                return isMainUser ? !!value : true;
            })
            .typeError("Birthdate is required field.")
            .min(minimalDate, "Maximal person age is 100.")
            .max(maximumDate, "Minimal person age is " + MIN_PERSON_BIRTHDATE_AGE + "."),
        email: yup
            .string()
            .when("id", requiredOnExisting("email"))
            .test("email", "Invalid email format", value => {
                return REGEX_EMAIL.test(value || "");
            }),
        // phone: yup
        //     .string()
        //     .when("id", requiredOnExisting("phone"))
        //     .phone(selectedCountryForPhone?.textCode as CountryCode)
        //     .test("startsWithPlus", "The phone number must start with the country code " + selectedCountryForPhone?.code, function (value) {
        //         return (value || "").startsWith(selectedCountryForPhone?.code);
        //     }),
        phone: yup
            .string()
            .when("id", requiredOnExisting("phone"))
            .phone(selectedCountryForPhone?.textCode as CountryCode)
            .test("startsWithPlus", t("The phone number must start with the country code ") + selectedCountryForPhone?.code, function (value) {
                return (value || "").startsWith(selectedCountryForPhone?.code);
            })
            .test(
                "startsWithPlusAndGoogleVerify",
                t("Bad phone number format. The phone number must be mobile with international prefix ") + selectedCountryForPhone?.code,
                function (value) {
                    let phoneNumber;
                    try {
                        phoneNumber = phoneUtil.parseAndKeepRawInput(value, selectedCountryForPhone?.textCode);
                    } catch (err) {
                        return false;
                    }
                    return phoneUtil.isValidNumber(phoneNumber) && phoneUtil.getNumberType(phoneNumber) === PhoneNumberType.MOBILE;
                }
            )
            .test("mobileNumberBadFormatCH", t("Bad number format"), function (value) {
                if (selectedCountryForPhone?.textCode === "CH") {
                    return (value || "").startsWith(selectedCountryForPhone?.code + "7");
                }
                return true;
            }),
        packages_cart_item_id: yup.number(),
        parent_user_id: yup.number().nullable(),
    });

    const FIELD_VALIDATIONS = [
        {field: "name", messageKey: t("Firstname is a required field.")},
        {field: "last_name", messageKey: t("Lastname is a required field.")},
        {field: "nickname", messageKey: t("Nickname is a required field.")},
        {field: "birthdate", messageKey: t("Birthdate is a required field.")},
        {field: "address", messageKey: t("Address is a required field.")},
        {field: "zip", messageKey: t("ZIP is a required field.")},
        {field: "town", messageKey: t("Town is a required field.")},
        {field: "country", messageKey: t("Country is a required field.")},
        {field: "email", messageKey: t("Email is a required field.")},
        {field: "phone", messageKey: t("Phone is a required field.")},
    ];

    // const isFirstDriverInReservation = context?.driversInReservation.length === 0;
    const isFirstDriverInReservation = false;
    const formDefaultEmailCheck = {
        emailCheck: props?.reservationDriver?.driver?.email
            ? props.reservationDriver.driver.email
            : isFirstDriverInReservation && user?.email
              ? user.email
              : undefined,
    };

    const formDefaultValues = {
        id: props.reservationDriver?.id ?? null,
        name: props?.reservationDriver?.driver?.name ? props.reservationDriver.driver.name : isFirstDriverInReservation ? user?.name : "",
        last_name: props?.reservationDriver?.driver?.last_name ? props.reservationDriver.driver.last_name : isFirstDriverInReservation ? user?.last_name : "",
        nickname: props?.reservationDriver?.driver?.nickname ? props.reservationDriver.driver.nickname : isFirstDriverInReservation ? user?.nickname : "",
        birthdate: props?.reservationDriver?.driver?.birthdate ? props.reservationDriver.driver.birthdate : isFirstDriverInReservation ? user?.birthdate : "",
        address: props?.reservationDriver?.driver?.address ? props.reservationDriver.driver.address : isFirstDriverInReservation ? user?.address : "",
        zip: props?.reservationDriver?.driver?.zip ? props.reservationDriver.driver.zip : isFirstDriverInReservation ? user?.zip : "",
        town: props?.reservationDriver?.driver?.town ? props.reservationDriver.driver.town : isFirstDriverInReservation ? user?.town : "",
        country: props?.reservationDriver?.driver?.country
            ? props.reservationDriver.driver.country
            : isFirstDriverInReservation
              ? user?.country
              : defaultCountryForSelect.name,
        email: props?.reservationDriver?.driver?.email
            ? props.reservationDriver.driver.email
            : isFirstDriverInReservation && user?.email
              ? user.email
              : undefined,
        phone: props?.reservationDriver?.driver?.phone
            ? props.reservationDriver.driver.phone
            : isFirstDriverInReservation && user?.phone
              ? user.phone
              : undefined,
        packages_cart_item_id: props.item.id,
        parent_user_id: props?.reservationDriver?.driver?.id != session?.uid ? session?.uid : null,
    };
    const hasDriverEmail = !!formDefaultValues?.email;
    const {
        watch,
        register,
        handleSubmit,
        setValue,
        setError,
        clearErrors,
        reset,
        getValues,
        formState: {errors},
        // } = useForm<LoginReservationCustomerFormInputs({
        // } = useForm<hasDriverEmail ? LoginReservationCustomerFormInputs : LoginReservationEmailCheckInputs>({
    } = useForm<any>({
        resolver: yupResolver(hasDriverEmail ? AccountMainSchema : AccountEmailCheckSchema),
        defaultValues: {...formDefaultValues},
    });
    const watchCountry = watch("country");

    const setPhone = value => {
        setValue("phone", value);
    };

    const birthdate = watch("birthdate");

    const [showEmailVerification, setShowEmailVerification] = useState<boolean>(!hasDriverEmail && !getValues("email"));
    const [showPresetOptions, setShowPresetOptions] = useState<boolean>(showEmailVerification);
    // const isEmailDisabled: boolean = (formDefaultValues?.email || getValues("email")) && (formDefaultValues?.email == session?.user?.email || getValues("email") == session?.user?.email);
    const {options} = usePersonTypeOptions();

    const editHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
    };

    const onSubmitErrorHandler = (data: any) => {
        consoleLog("onSubmitErrorHandler", data);
    };

    const fetchReservationHandler = () => {
        if (!selectedReservation) {
            return;
        }
        dispatch(BookingLoginAction.fetchReservationDetail(selectedReservation.id))
            .then(r => {})
            .catch(err => {
                console.log(err);
            });
    };

    const setAccessItemHandler = (value: number) => {
        setValue("packages_cart_item_id", value);
    };

    const onSubmitSaveHandler = (data: LoginReservationCustomerFormInputs) => {
        if (isLoading) {
            return true;
        }
        if (!selectedReservation || !context) {
            return true;
        }

        setIsLoading(true);
        if (!data?.phone || (data?.phone && data?.phone === selectedCountryForPhone?.code)) {
            delete data.phone;
        }
        if (!data?.email) {
            delete data.email;
        }

        const {birthdate, ...rest} = data;
        const birthdateYMD = Dates.ymd(birthdate, "YYYY-MM-DD");
        if (props?.reservationDriver?.driver) {
            const updatedCustomerRequest: UpdateCustomerFromReservationNewRequest = {
                reservation: selectedReservation,
                customer: {
                    driver_id: props.reservationDriver?.driver.id,
                    reservation_driver_id: props.reservationDriver?.id,
                    user_type: props.reservationDriver?.driver.user_type,
                    ...rest,
                    birthdate: birthdateYMD,
                    packages_cart_item_id: data.packages_cart_item_id,
                    parent_user_id: data.parent_user_id,
                },
            };
            return dispatch(BookingLoginAction.fetchUpdateCustomerFromReservationNew(updatedCustomerRequest))
                .then(response => {
                    if (response?.driver) {
                        dispatch(BookingLoginAction.setDriverFamilyAccounts(response.driver));
                    }
                    return context.saveDriver(true);
                })
                .finally(() => {
                    setIsEditing(false);
                    setIsLoading(false);
                    fetchReservationHandler();
                });
        } else {
            const newCustomerRequest: SetCustomerToReservationNewRequest = {
                reservation: selectedReservation,
                customer: {
                    ...data,
                    birthdate: birthdateYMD,
                    user_type: isCustomerUserTypeMain ? UserType.User : UserType.Guest,
                    packages_cart_item_id: data.packages_cart_item_id,
                    parent_user_id: data.parent_user_id,
                },
            };

            const customerData = newCustomerRequest.customer;

            const validateField = (field: string, messageKey: string, customerData: any) => {
                if (!customerData[field] && requiredFields.includes(field)) {
                    setError(
                        field,
                        {
                            type: "required",
                            message: messageKey,
                        },
                        {shouldFocus: true}
                    );

                    return false;
                }
                return true;
            };

            // Perform validation for all fields
            const isValid = FIELD_VALIDATIONS.every(({field, messageKey}) => validateField(field, messageKey, customerData));
            if (!isValid) {
                setIsEditing(false);
                setIsLoading(false);
                return;
            }

            return dispatch(BookingLoginAction.fetchSetCustomerToReservationNew(newCustomerRequest))
                .then(response => {
                    return context.saveDriver(true);
                })
                .finally(() => {
                    setIsEditing(false);
                    setIsLoading(false);
                    fetchReservationHandler();
                });
        }
    };

    const onCancelChangesHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setResetCountry(true);
        setResetPhone(true);
        reset({...formDefaultValues});
        clearErrors();
        setIsEditing(false);
        context?.saveDriver(true);
    };

    const openModalHandler = e => {
        e.preventDefault();
        e.stopPropagation();
        if (context?.openModal && props?.reservationDriver) {
            context.openModal(props.reservationDriver);
        }
    };

    const onSubmitCheckEmailErrorHandler = (data: any) => {
        consoleLog("onSubmitCheckEmailErrorHandler", data);
    };

    const setShowGuestPhoneAndEmailHandler = (is_adult: boolean) => {
        setShowGuestPhoneAndEmail(false);
        setShowPresetOptions(false);
        if (!is_adult) {
            setIsChild(true);
            setValue("email", user?.email);
            setValue("emailCheck", user?.email);
            setValue("address", user?.address);
            setValue("phone", user?.phone);
            setValue("zip", user?.zip);
            setValue("town", user?.town);
            setShowEmailVerification(false);
        }
    };
    const onSubmitCheckEmailHandler = (data: LoginReservationEmailCheckInputs) => {
        consoleLog("onSubmitCheckEmailHandler", data);
        consoleLog("isLoading", isLoading);
        setEmailCheckInfoMessage(null);
        if (isLoading) {
            return true;
        }

        setIsLoading(true);
        // if (data.emailCheck === user?.email) {
        //     //show message that you are adding your child
        //     setValue("email", data.emailCheck);
        //     setIsLoading(false);
        //     setShowEmailVerification(false);
        // } else {
            dispatch(BookingLoginAction.fetchCheckCustomerEmail(data.emailCheck))
                .then(
                    onResolve => {
                        if (onResolve) {
                            setEmailCheckInfoMessage(t("Account with this email already exists"));
                        } else {
                            setValue("email", data.emailCheck);
                            // setIsEditing(true);
                            setShowEmailVerification(false);
                        }
                    },
                    onReject => {
                        consoleLog(onReject);
                    }
                )
                .finally(() => {
                    setIsLoading(false);
                });
        // }
    };

    const email = watch("emailCheck");

    const inviteUserHandler = () => {
        if (!selectedReservation) {
            return;
        }
        dispatch(
            BookingLoginAction.fetchReservationInvitation({
                email: email,
                reservation_id: selectedReservation.id,
                packages_cart_item_id: props.item.id,
            })
        );
    };

    useEffect(() => {
        //email has chagned
        if (emailCheckInfoMessage) {
            setEmailCheckInfoMessage(null);
        }
    }, [email]);

    useEffect(() => {
        setIsEditing(phoneNumberWasSuggested);
    }, [phoneNumberWasSuggested]);

    return (
        <div ref={context?.innerRef} className={clsx("AccessTicketEditGuest flex flex-col relative", context?.className)}>
            {showPresetOptions && (
                <div className={"PresetPersonTypeOptions flex flex-col w-full gap-3"}>
                    {options.map((option, i) => {
                        return <PresetPersonTypeOption key={i} option={option} onClick={(isAdult: boolean) => setShowGuestPhoneAndEmailHandler(isAdult)} />;
                    })}
                </div>
            )}
            {!showPresetOptions && showEmailVerification && (
                <form
                    className={clsx("w-full overflow-auto rounded-2xl shadow-sm border-2 border-gray-100")}
                    onSubmit={handleSubmit(onSubmitCheckEmailHandler, onSubmitCheckEmailErrorHandler)}
                >
                    <BookingLoginTableRow className={clsx("ReservationEmailVerification grid-cols-1 gap-4 xl:gap-y-4 xl:gap-x-8 px-6 xl:px-10 py-5 lg:py-6")}>
                        {/*<BookingLoginTableRow className={clsx("ReservationEmailVerification grid-cols-1 gap-4 xl:gap-y-4 lg:gap-px-6 xl:gap-x-8 px-6 lg:px-0 xl:px-10 py-5 lg:py-6")}>*/}
                        <TableRowColumn className={"CheckEmailInput w-full"}>
                            <ReservationDetailInput errors={errors} label={t("Email")} name={"emailCheck"} register={register} required={true} />
                        </TableRowColumn>

                        <TableRowColumn className={"ButtonActions w-full mt-2"}>
                            <BookingLoginReservationButton
                                className={"w-full"}
                                type={"submit"}
                                onClick={handleSubmit(onSubmitCheckEmailHandler, onSubmitCheckEmailErrorHandler)}
                            >
                                <ReservationButtonInner>
                                    <span>{t("Check")}</span>
                                    {isLoading ? <LoadingIcon className={"size-[16px] mr-1.5 flex-none"} /> : <CheckIcon className={"stroke-2 size-4"} />}
                                </ReservationButtonInner>
                            </BookingLoginReservationButton>
                        </TableRowColumn>

                        {emailCheckInfoMessage && (
                            <>
                                <BookingLoginTableRowColumn className={"CheckEmailDescription w-full flex flex-col"}>
                                    <p
                                        className={
                                            "flex text-red-500 text-sm bg-red-50 border border-dashed border-red-500 rounded-md px-2 py-1 justify-center"
                                        }
                                    >
                                        {emailCheckInfoMessage}
                                    </p>
                                </BookingLoginTableRowColumn>

                                <h4>{t("Do you want to invite this person?")}</h4>
                                <BookingLoginReservationButton className={"w-full"} type={"button"} onClick={inviteUserHandler}>
                                    <ReservationButtonInner>
                                        <span>{t("Invite")}</span>
                                        <ArrowRightIcon className={"stroke-2 size-4"} />
                                    </ReservationButtonInner>
                                </BookingLoginReservationButton>
                            </>
                        )}
                    </BookingLoginTableRow>
                </form>
            )}
            {!showPresetOptions && !showEmailVerification && (
                <form
                    autoComplete={"off"}
                    className={clsx("w-full overflow-auto rounded-2xl shadow-sm border-2 border-gray-100")}
                    onSubmit={handleSubmit(onSubmitSaveHandler, onSubmitErrorHandler)}
                >
                    <BookingLoginTableRow
                        // className={"grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 !items-start gap-x-4 md:gap-x-14 gap-y-4 px-6 md:px-20 pt-6 pb-6 md:pb-16"}
                        className={clsx(
                            "ReservationDriverForm grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 !items-start gap-4 xl:gap-y-4 xl:gap-x-8 px-6 xl:px-10 py-5 lg:py-6"
                        )}
                    >
                        <input {...register("reservation_driver_guid")} type={"hidden"} />

                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"First name"}
                            name={"name"}
                            register={register}
                            required={requiredFields.includes("name")}
                        />
                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"Last name"}
                            name={"last_name"}
                            register={register}
                            required={requiredFields.includes("last_name")}
                        />
                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"Nickname"}
                            name={"nickname"}
                            register={register}
                            required={requiredFields.includes("nickname")}
                        />

                        <Birthdate
                            disabled={isDisabled}
                            defaultValues={formDefaultValues}
                            name={"birthdate"}
                            errors={errors}
                            isChild={isChild}
                            register={register}
                            label={t("Birthdate")}
                            setValue={setValue}
                            required={requiredFields.includes("birthdate")}
                        />

                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"Address"}
                            name={"address"}
                            register={register}
                            required={requiredFields.includes("address")}
                        />

                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"Zip"}
                            name={"zip"}
                            register={register}
                            required={requiredFields.includes("zip")}
                        />

                        <ReservationDetailInput
                            disabled={isDisabled}
                            errors={errors}
                            label={"Town"}
                            name={"town"}
                            register={register}
                            required={requiredFields.includes("town")}
                        />

                        <ReservationDetailCountry
                            disabled={isDisabled}
                            defaultCountry={defaultCountryForSelect}
                            errors={errors}
                            label={"Country"}
                            name={"country"}
                            options={filteredSpecificCountryFirst(["CH", "LI", "DE", "AT", "FR", "IT"], countryList)}
                            register={register}
                            required={requiredFields.includes("country")}
                            // required={true}
                            setValue={setValue}
                            value={watchCountry}
                        />
                        <ReservationDetailInput disabled={true} errors={errors} label={"Email"} name={"email"} register={register} required={true} />
                        <ReservationDetailPhoneWithCountrySelect
                            disabled={isDisabled}
                            defaultCountry={selectedCountryForPhone}
                            errors={errors}
                            label={t("Mobile phone")}
                            name={"phone"}
                            options={countryList}
                            register={register}
                            // required={true}
                            required={requiredFields.includes("phone")}
                            setSelectedCountry={setSelectedCountryForPhone}
                            setValue={setPhone}
                            value={formDefaultValues?.phone}
                        />
                        <TableRowColumn
                            className={
                                // "InvitationActions mt-4 self-end lg:mt-0 col-span-1 md:col-start-3 lg:col-start-3 xl:col-start-3 flex flex-row items-center justify-end gap-x-2.5"
                                "InvitationActions mt-8 self-end col-span-1 lg:col-start-2 xl:col-start-3 flex flex-row items-center justify-end"
                            }
                        >
                            <BookingLoginReservationButton
                                className={"w-full mx-auto"}
                                disabled={isLoading}
                                onClick={handleSubmit(onSubmitSaveHandler, onSubmitErrorHandler)}
                            >
                                <ReservationButtonInner>
                                    <span>{t("Save")}</span>
                                    {isLoading ? (
                                        <LoadingIcon className={"stroke-2 size-4"} />
                                    ) : (
                                        <FeatherIcon name={"arrow-right"} strokeWidth={2} className={"stroke-2 size-4"} />
                                    )}
                                </ReservationButtonInner>
                            </BookingLoginReservationButton>
                        </TableRowColumn>
                    </BookingLoginTableRow>
                    {phoneNumberWasSuggested && (
                        <BookingLoginTableRow className={"grid-cols-1"}>
                            <TableRowColumn className={"!py-0 mb-4 md:mb-2 px-4 sm:px-6 lg:px-8"}>
                                <div
                                    className={
                                        "flex flex-row items-center border border-dashed px-2 py-1.5 rounded-lg w-full bg-red-50 border-red-400 text-red-500 text-center justify-center"
                                    }
                                >
                                    <FeatherIcon name={"exclamation"} strokeWidth={2} className={"flex-none size-4 text-red-400 mr-1.5"} />
                                    <span className={"text-sm"}>
                                        {t(
                                            "Phone number has wrong format. We suggested new phone number format with international country prefix start with symbol +. If wrong please change it and save."
                                        )}
                                    </span>
                                </div>
                            </TableRowColumn>
                        </BookingLoginTableRow>
                    )}
                </form>
            )}
        </div>
    );
};

export default AccessTicketEditGuest;
